import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Step 1: Strategic Question Types
export interface StrategicQuestion {
  questionId: string;
  question: string;
  category: string;
  priority: 'high' | 'medium' | 'low';
}

export interface DataSource {
  name?: string;
  source?: string;
  system_type?: string;
  confidence?: number;
}

export interface ConsultingAnalysis {
  questionId: string;
  category: string;
  data_sources?: {
    available?: DataSource[];
    needs_validation?: DataSource[];
    gaps?: (string | DataSource)[];
  };
  hypotheses?: string[];
  recommended_approach?: {
    phases?: Array<{
      phase: string;
      description: string;
    }>;
  };
}

// ============================================================================
// Multi-System Data Canvas Types (Step 3 Enhancement)
// ============================================================================

export type SystemRole = 'primary' | 'supporting' | 'reference';
export type ConnectionPointType = 'direct_key' | 'derived_key' | 'semantic' | 'temporal';
export type ColumnPriority = 'must' | 'should' | 'could';

export interface CategorizedColumn {
  column_name: string;
  data_type?: string;
  is_primary_key?: boolean;
  is_foreign_key?: boolean;
  is_nullable?: boolean;
  priority: ColumnPriority;
  reason: string;
  description?: string;
}

export interface CategorizedColumns {
  must: CategorizedColumn[];
  should: CategorizedColumn[];
  could: CategorizedColumn[];
}

export interface TableMapping {
  table_id?: string;
  table_name: string;
  table_description?: string;
  module?: string;
  columns: CategorizedColumns;
  confidence?: number;
  rationale?: string;
  approved?: boolean;
  notes?: string;
}

export interface SystemDataSource {
  system_id: string;
  system_name: string;
  vendor: string;
  functional_area?: string;
  role: SystemRole;
  role_description?: string;
  tables: TableMapping[];
  approved?: boolean;
}

export interface ConnectionPoint {
  connection_id: string;
  source_system_id: string;
  source_system_name: string;
  source_table: string;
  source_column: string;
  target_system_id: string;
  target_system_name: string;
  target_table: string;
  target_column: string;
  connection_type: ConnectionPointType;
  transformation_rule?: string;
  cardinality: string;
  description?: string;
  validated?: boolean;
}

export interface QuestionDataCanvas {
  canvas_id: string;
  case_id: string;
  question_id: string;
  question_text: string;
  question_category?: string;
  data_sources: SystemDataSource[];
  connection_points: ConnectionPoint[];
  status: 'draft' | 'in_review' | 'approved' | 'rejected';
  total_systems: number;
  total_tables: number;
  total_columns: number;
  total_connections: number;
  approved?: boolean;
  // AI background-validation verdict (fully-automatic approval flow). Canvases
  // are auto-approved on generation; 'flag' surfaces a non-blocking "lower
  // confidence" badge instead of an approve/reject action.
  validation_verdict?: 'approve' | 'flag';
  consultant_notes?: string;
  created_at?: string;
  updated_at?: string;
}

// Enterprise System Selection Types
export interface EnterpriseSystem {
  systemId: string;
  name: string;
  type: 'ERP' | 'CRM' | 'SCM' | 'WMS' | 'MES' | 'Custom';
  vendor?: string;
  version?: string;
  selected: boolean;
  // Optional enrichment from the backend.  Used to compute the per-category
  // "Recommended" chip on Step 2 — absent values just lower the score
  // contribution without blocking selection.
  /** 'library' | 'llm_generated' | 'user_uploaded' | 'manual_seed' */
  source?: string;
  /** Industries this system explicitly targets (used for industry-match scoring). */
  targetIndustries?: string[];
  /** Schema completeness signal — systems with 0 tables can't be mapped in Step 3. */
  totalTables?: number;
}

// Data Mapping Types
export interface DataMapping {
  mappingId: string;
  questionId: string;
  questionText: string;
  sourceSystem: string;
  sourceTable: string;
  sourceColumns: string[];
  targetEntity: string;
  targetProperties: string[];
  confidence: number;
  transformationRules?: string;
  approved: boolean;
}

// Schema Design Types
export interface SchemaNode {
  label: string;
  properties: Array<{
    name: string;
    type: string;
    required: boolean;
    unique?: boolean;
    indexed?: boolean;
  }>;
}

export interface SchemaRelationship {
  type: string;
  from: string;
  to: string;
  properties?: Array<{
    name: string;
    type: string;
  }>;
}

export interface SchemaDesign {
  nodes: SchemaNode[];
  relationships: SchemaRelationship[];
  constraints: string[];
  indexes: string[];
  cypherDDL?: string;
}

/**
 * A server SchemaPreview, held structurally so this store stays free of a
 * dependency on the API layer's types. The shape mirrors `SchemaPreview` in
 * `types/knowledgeGraphDesign.ts`; the index signature preserves any field the
 * backend adds, since these objects are forwarded to /schema/complete verbatim
 * rather than being read field-by-field.
 */
export interface SchemaPreviewLike {
  case_id?: string;
  system_id?: string;
  tables?: any[];
  relationships?: any[];
  mermaid_diagram?: string;
  cypher_ddl?: string;
  coverage_analysis?: any;
  gds_recommendations?: any[];
  [key: string]: any;
}

interface AnalysisState {
  // Scope marker — which caseId the persisted state belongs to.  The wizard
  // checks this on mount: same caseId → resume (keep canvases/mappings);
  // different caseId → reset before adopting the new case.  Without this,
  // the global localStorage key would leak Case A's mappings into Case B,
  // or get wiped when the wizard auto-resets on every fresh mount.
  caseId: string | null;

  // Secondary scope marker: the case's createdAt.
  //
  // caseId ALONE is not unique over time. The backend reuses ids like
  // "proj_001", so wiping the database and creating a new case produces a
  // DIFFERENT case with the SAME id. The scope check then read
  // `storedCaseId === caseId` as "resuming the same case" and kept the old
  // case's canvases — which is how an oil-refinery schema (FeedstockPlan,
  // BlendSchedule) rendered inside a consumer-goods case whose Neo4j data
  // contained no such tables at all.
  //
  // createdAt distinguishes them: same id + different creation time = a
  // different case, so reset. Null on state persisted by an older build,
  // which is treated as "unknown" and therefore reset (safe direction).
  caseCreatedAt: string | null;

  // Step 1: Strategic Questions & Analysis
  strategicQuestions: StrategicQuestion[];
  consultingAnalyses: ConsultingAnalysis[];

  // Step 2: Enterprise Systems
  enterpriseSystems: EnterpriseSystem[];
  customSystems: string[];
  selectedSystemIds: string[];  // IDs of systems from research library

  // Step 3: Data Mappings (Legacy - single system per question)
  dataMappings: DataMapping[];

  // Step 3: Multi-System Data Canvas (Enhanced)
  questionCanvases: QuestionDataCanvas[];
  activeCanvasId: string | null;

  // Step 4: Schema Design
  schemaDesign: SchemaDesign | null;

  // The RAW server SchemaPreview(s) behind `schemaDesign`, kept verbatim.
  //
  // `schemaDesign` is a lossy UI projection: convertSchemaPreviewToDesign()
  // drops tables → nodes, and discards mermaid_diagram, coverage_analysis and
  // gds_recommendations entirely. POST .../schema/complete requires those
  // fields — sending the projection produced an empty CompletedSchema, which
  // is why the Design phase had nothing to read. Keep the server's own payload
  // so completion can forward it unmodified.
  schemaPreviews: SchemaPreviewLike[];

  // Step 1 Actions
  setStrategicQuestions: (questions: StrategicQuestion[]) => void;
  setConsultingAnalyses: (analyses: ConsultingAnalysis[]) => void;

  // Step 2 Actions
  setEnterpriseSystems: (systems: EnterpriseSystem[]) => void;
  toggleSystemSelection: (systemId: string) => void;
  addCustomSystem: (systemName: string) => void;
  removeCustomSystem: (systemName: string) => void;
  setSelectedSystemIds: (ids: string[]) => void;
  addSelectedSystemId: (id: string) => void;
  removeSelectedSystemId: (id: string) => void;

  // Step 3 Actions (Legacy)
  setDataMappings: (mappings: DataMapping[]) => void;
  updateDataMapping: (mappingId: string, updates: Partial<DataMapping>) => void;
  toggleMappingApproval: (mappingId: string) => void;

  // Step 3 Actions (Multi-System Canvas)
  setQuestionCanvases: (canvases: QuestionDataCanvas[]) => void;
  addQuestionCanvas: (canvas: QuestionDataCanvas) => void;
  updateQuestionCanvas: (canvasId: string, updates: Partial<QuestionDataCanvas>) => void;
  removeQuestionCanvas: (canvasId: string) => void;
  setActiveCanvasId: (canvasId: string | null) => void;

  // Canvas Data Source Actions
  addCanvasDataSource: (canvasId: string, dataSource: SystemDataSource) => void;
  updateCanvasDataSourceRole: (canvasId: string, systemId: string, role: SystemRole, roleDescription?: string) => void;
  removeCanvasDataSource: (canvasId: string, systemId: string) => void;

  // Canvas Connection Actions
  addCanvasConnection: (canvasId: string, connection: ConnectionPoint) => void;
  removeCanvasConnection: (canvasId: string, connectionId: string) => void;

  // Canvas Approval Actions
  approveCanvas: (canvasId: string, notes?: string) => void;
  rejectCanvas: (canvasId: string, notes?: string) => void;

  // Step 4 Actions
  setSchemaDesign: (schema: SchemaDesign) => void;
  setSchemaPreviews: (previews: SchemaPreviewLike[]) => void;

  // Scope
  setCaseId: (caseId: string | null, caseCreatedAt?: string | null) => void;

  // Reset
  resetAnalysis: () => void;
}

const initialState = {
  caseId: null as string | null,
  caseCreatedAt: null as string | null,
  strategicQuestions: [] as StrategicQuestion[],
  consultingAnalyses: [] as ConsultingAnalysis[],
  enterpriseSystems: [] as EnterpriseSystem[],
  customSystems: [] as string[],
  selectedSystemIds: [] as string[],
  dataMappings: [] as DataMapping[],
  questionCanvases: [] as QuestionDataCanvas[],
  activeCanvasId: null as string | null,
  schemaDesign: null as SchemaDesign | null,
  schemaPreviews: [] as SchemaPreviewLike[],
};

export const useAnalysisStore = create<AnalysisState>()(
  persist(
    (set) => ({
  ...initialState,

  // Step 1 Actions
  setStrategicQuestions: (questions) => set({ strategicQuestions: questions }),
  setConsultingAnalyses: (analyses) => set({ consultingAnalyses: analyses }),

  // Enterprise Systems Actions
  setEnterpriseSystems: (systems) => set({ enterpriseSystems: systems }),

  toggleSystemSelection: (systemId) => set((state) => ({
    enterpriseSystems: state.enterpriseSystems.map(sys =>
      sys.systemId === systemId ? { ...sys, selected: !sys.selected } : sys
    )
  })),

  addCustomSystem: (systemName) => set((state) => ({
    customSystems: [...state.customSystems, systemName]
  })),

  removeCustomSystem: (systemName) => set((state) => ({
    customSystems: state.customSystems.filter(s => s !== systemName)
  })),

  // Selected System IDs (from research library)
  setSelectedSystemIds: (ids) => set({ selectedSystemIds: ids }),

  addSelectedSystemId: (id) => set((state) => ({
    selectedSystemIds: state.selectedSystemIds.includes(id)
      ? state.selectedSystemIds
      : [...state.selectedSystemIds, id]
  })),

  removeSelectedSystemId: (id) => set((state) => ({
    selectedSystemIds: state.selectedSystemIds.filter(sid => sid !== id)
  })),

  // Data Mappings Actions
  setDataMappings: (mappings) => set({ dataMappings: mappings }),

  updateDataMapping: (mappingId, updates) => set((state) => ({
    dataMappings: state.dataMappings.map(m =>
      m.mappingId === mappingId ? { ...m, ...updates } : m
    )
  })),

  toggleMappingApproval: (mappingId) => set((state) => ({
    dataMappings: state.dataMappings.map(m =>
      m.mappingId === mappingId ? { ...m, approved: !m.approved } : m
    )
  })),

  // Schema Design Actions
  setSchemaDesign: (schema) => set({ schemaDesign: schema }),

  setSchemaPreviews: (previews) => set({ schemaPreviews: previews }),

  // ============================================================================
  // Multi-System Canvas Actions
  // ============================================================================

  // Canvas CRUD
  setQuestionCanvases: (canvases) => set({ questionCanvases: canvases }),

  addQuestionCanvas: (canvas) => set((state) => ({
    questionCanvases: [...state.questionCanvases, canvas]
  })),

  updateQuestionCanvas: (canvasId, updates) => set((state) => ({
    questionCanvases: state.questionCanvases.map(c =>
      c.canvas_id === canvasId ? { ...c, ...updates } : c
    )
  })),

  removeQuestionCanvas: (canvasId) => set((state) => ({
    questionCanvases: state.questionCanvases.filter(c => c.canvas_id !== canvasId),
    activeCanvasId: state.activeCanvasId === canvasId ? null : state.activeCanvasId
  })),

  setActiveCanvasId: (canvasId) => set({ activeCanvasId: canvasId }),

  // Canvas Data Source Actions
  addCanvasDataSource: (canvasId, dataSource) => set((state) => ({
    questionCanvases: state.questionCanvases.map(c => {
      if (c.canvas_id !== canvasId) return c;
      return {
        ...c,
        data_sources: [...c.data_sources, dataSource],
        total_systems: c.total_systems + 1,
        total_tables: c.total_tables + dataSource.tables.length,
        total_columns: c.total_columns + dataSource.tables.reduce(
          (sum, t) => sum + t.columns.must.length + t.columns.should.length + t.columns.could.length, 0
        )
      };
    })
  })),

  updateCanvasDataSourceRole: (canvasId, systemId, role, roleDescription) => set((state) => ({
    questionCanvases: state.questionCanvases.map(c => {
      if (c.canvas_id !== canvasId) return c;
      return {
        ...c,
        data_sources: c.data_sources.map(ds =>
          ds.system_id === systemId ? { ...ds, role, role_description: roleDescription } : ds
        )
      };
    })
  })),

  removeCanvasDataSource: (canvasId, systemId) => set((state) => ({
    questionCanvases: state.questionCanvases.map(c => {
      if (c.canvas_id !== canvasId) return c;
      const removedSource = c.data_sources.find(ds => ds.system_id === systemId);
      if (!removedSource) return c;

      const removedTables = removedSource.tables.length;
      const removedColumns = removedSource.tables.reduce(
        (sum, t) => sum + t.columns.must.length + t.columns.should.length + t.columns.could.length, 0
      );

      return {
        ...c,
        data_sources: c.data_sources.filter(ds => ds.system_id !== systemId),
        total_systems: Math.max(0, c.total_systems - 1),
        total_tables: Math.max(0, c.total_tables - removedTables),
        total_columns: Math.max(0, c.total_columns - removedColumns),
        // Also remove connections involving this system
        connection_points: c.connection_points.filter(
          cp => cp.source_system_id !== systemId && cp.target_system_id !== systemId
        ),
        total_connections: c.connection_points.filter(
          cp => cp.source_system_id !== systemId && cp.target_system_id !== systemId
        ).length
      };
    })
  })),

  // Canvas Connection Actions
  addCanvasConnection: (canvasId, connection) => set((state) => ({
    questionCanvases: state.questionCanvases.map(c => {
      if (c.canvas_id !== canvasId) return c;
      return {
        ...c,
        connection_points: [...c.connection_points, connection],
        total_connections: c.total_connections + 1
      };
    })
  })),

  removeCanvasConnection: (canvasId, connectionId) => set((state) => ({
    questionCanvases: state.questionCanvases.map(c => {
      if (c.canvas_id !== canvasId) return c;
      return {
        ...c,
        connection_points: c.connection_points.filter(cp => cp.connection_id !== connectionId),
        total_connections: Math.max(0, c.total_connections - 1)
      };
    })
  })),

  // Canvas Approval Actions
  approveCanvas: (canvasId, notes) => set((state) => ({
    questionCanvases: state.questionCanvases.map(c => {
      if (c.canvas_id !== canvasId) return c;
      return {
        ...c,
        status: 'approved' as const,
        approved: true,
        consultant_notes: notes || c.consultant_notes,
        updated_at: new Date().toISOString()
      };
    })
  })),

  rejectCanvas: (canvasId, notes) => set((state) => ({
    questionCanvases: state.questionCanvases.map(c => {
      if (c.canvas_id !== canvasId) return c;
      return {
        ...c,
        status: 'rejected' as const,
        approved: false,
        consultant_notes: notes || c.consultant_notes,
        updated_at: new Date().toISOString()
      };
    })
  })),

  // Reset
  setCaseId: (caseId, caseCreatedAt = null) => set({ caseId, caseCreatedAt }),
  resetAnalysis: () => set(initialState),
    }),
    {
      name: 'analysis-storage',
      partialize: (state) => ({
        // Persist only essential state for page refresh.
        // caseId is part of this set so the wizard can detect on mount
        // whether the persisted state belongs to the case being opened.
        caseId: state.caseId,
        caseCreatedAt: state.caseCreatedAt,
        enterpriseSystems: state.enterpriseSystems,
        customSystems: state.customSystems,
        selectedSystemIds: state.selectedSystemIds,
        questionCanvases: state.questionCanvases,
        activeCanvasId: state.activeCanvasId,
        dataMappings: state.dataMappings,
        // Step 4 was absent here, so a refresh on Schema Design dropped the
        // generated schema while other panels (reading their own query cache)
        // still reported it — the "Saved Schema Loaded: 15 nodes" banner
        // sitting above "No schema generated yet".
        schemaDesign: state.schemaDesign,
        schemaPreviews: state.schemaPreviews,
      }),
    }
  )
);
